import { apiInternals, send } from './client';

/**
 * `request()` is not itself exported from `client.ts` — only `send()` and
 * `apiInternals` are. Needed here for exactly one endpoint below,
 * `versions()`: the identical reason `cases.ts`/`api.auditLog()` state for
 * their own `apiInternals.request` import — reading `meta` as well as `data`.
 */
const { request } = apiInternals;

/**
 * The case's message history and the send picker's catalogue (M-08, Task 8)
 * — `GET /sms-templates`, `GET /cases/{case}/messages`, `POST /cases/{case}/
 * messages` and `GET /sms-messages/{message}`, verified field-for-field
 * against `SmsTemplateResource`/`SmsMessageResource` in the API repository,
 * not inferred from this task's own brief.
 *
 * Plus the DIRECT-SEND pair — `POST /sms-messages` and `GET /sms-messages`,
 * a message sent straight to a mobile number with no case (`sendDirect()`/
 * `direct()` below). `direct()` is the one exception to the next section:
 * it IS cursor-paginated, so it is built on `apiInternals.request`.
 *
 * ## All four are `send<T>()`, and that is deliberate, not an oversight
 *
 * Every one of these four answers a PLAIN resource — a single row, or an
 * `AnonymousResourceCollection` (`SmsTemplateController::index()`,
 * `CaseMessageController::index()`) with only `data`, no `meta` at all —
 * unlike `casesApi.list()`/`codesApi.list()`, which are cursor-paginated and
 * therefore built on `apiInternals.request` so `meta.next_cursor` is not
 * thrown away. NONE of these four endpoints is cursor-paginated (the
 * template catalogue is 28 seeded rows served whole,
 * `SmsTemplateController`'s own docblock; a case's message history is
 * fetched whole too), so there is no `meta` here for `send()` to lose. A
 * later reader who knows `casesApi.list()`'s trap should NOT "fix" this
 * module to route through `apiInternals.request` instead — there is nothing
 * for it to recover, and doing so would just add an unused `meta` nobody
 * reads.
 *
 * ## `SmsMessageRow` deliberately excludes `provider_response`
 *
 * `SmsMessageResource` itself never emits it (that resource's own
 * docblock, Task 6 ruling 4) — the raw gateway payload is an
 * implementation detail for troubleshooting a failed send, read from
 * `sms_messages` directly by whoever needs it, not a field a case's
 * message-history panel renders. Adding it here would type a field the
 * wire never actually sends.
 *
 * ## `SmsTemplate` is all nine of `SmsTemplateResource`'s fields (fix round 1)
 *
 * The first pass of this module typed only `{code, name, category}` — the
 * three fields the send picker's `Select` uses — and left `uuid`,
 * `description`, `channel`, `locale`, `body` and `variables` untyped despite
 * the resource sending all nine. `body`/`variables` are not incidental:
 * `SmsTemplateResource`'s own docblock says they are exposed SPECIFICALLY so
 * a send screen can show what is about to go to a patient before it is
 * sent — "which placeholders a template still needs filled" and "what the
 * rendered text will look like around them". `SendMessageDialog` reads both
 * to render that preview; typing fewer than nine fields here would silently
 * make that preview impossible to build correctly.
 *
 * `variables` IS A MAP, NOT A LIST (`SmsTemplate` model's own docblock,
 * verified in the API repository): each declared placeholder name maps to
 * one of exactly three states — `'required'`, `'optional'`, or `'line'`
 * (a value only ever appended as a whole line when truthy, transcribed
 * from the legacy `if ($pdf) …` guards — see `SmsTemplateSeeder`'s own
 * comments). `SmsTemplateVariableState` below is a closed union of those
 * three, not `string`, so a caller cannot compile a branch for a fourth
 * state that will never arrive.
 *
 * ## `delivered` and `rejected` are not statuses this module names (fix
 * round — final whole-branch review, Minor 3)
 *
 * `SmsMessage.status` is a six-value schema enum — `queued`, `sending`,
 * `sent`, `delivered`, `failed`, `rejected` — but `SendSmsMessage::handle()`
 * (the ONE place anything ever writes this column, per `SmsDispatcher`'s own
 * docblock) only ever forces it to `'sending'`, `'sent'`, or `'failed'`.
 * `delivered` is unreachable because Unifonic delivery receipts are not
 * wired (S-5) — nothing on the API side can ever set it, and the API
 * repository pins this with its own test; `SmsMessageStatus` below leaves it
 * out entirely. `rejected` is EQUALLY unreachable, for a different reason: a
 * gateway rejection is recorded as `'sending'` (not yet the final attempt —
 * see that method's own docblock for why an intermediate rejection must
 * stay retryable) or `'failed'` (the final attempt), never as its own
 * distinct status; nothing in this codebase's write path ever assigns
 * `'rejected'` at all. Unlike `delivered`, `rejected` IS kept in the union
 * below and in `sms.status.rejected` (`CaseMessages.tsx`'s `STATUS_CLASS`
 * keys on the full union) — this fix corrects only what this docblock
 * claims about reachability, not the type or the translation catalogue; no
 * caller of this module should expect either value to actually arrive.
 */

/** `variables`' three states — see this module's own docblock. */
export type SmsTemplateVariableState = 'required' | 'optional' | 'line';

/**
 * One template on the send picker's catalogue, exactly as
 * `SmsTemplateResource` renders it — all nine fields, verified
 * field-for-field against that class in the API repository.
 */
export interface SmsTemplate {
  uuid: string;
  code: string;
  name: string;
  description: string | null;
  channel: string;
  locale: string;
  /**
   * The RAW template, `{{placeholders}}` still in it — rendered against a
   * case only at send time (`SmsDispatcher`), never by this application. A
   * send screen previewing this is previewing the SHAPE of the message,
   * not the final text the patient will read.
   */
  body: string;
  /** Placeholder name -> one of three states. See this module's own docblock. */
  variables: Readonly<Record<string, SmsTemplateVariableState>>;
  category: string;
}

/** `SmsMessageResource`'s `template` key — enough to say WHICH message this was. */
export interface SmsMessageTemplateRef {
  code: string;
  name: string;
}

/**
 * Every status `SmsMessage.status` can answer over the wire — see this
 * module's own docblock for why `delivered` is not among them.
 */
export type SmsMessageStatus = 'queued' | 'sending' | 'sent' | 'failed' | 'rejected';

/**
 * One outbound message, exactly as `SmsMessageResource` renders it for all
 * three of `GET /cases/{case}/messages`, `POST /cases/{case}/messages` and
 * `GET /sms-messages/{message}` — the one shape all three share (that
 * resource's own class docblock).
 */
export interface SmsMessageRow {
  uuid: string;
  template: SmsMessageTemplateRef | null;
  status: SmsMessageStatus;
  recipient: string;
  rendered_body: string;
  failure_reason: string | null;
  /** ISO 8601. Always present — a message cannot exist without having been queued. */
  queued_at: string;
  /** ISO 8601, or `null` while the message has not (yet, or ever) sent. */
  sent_at: string | null;
  /** ISO 8601, or `null` while the message has not failed. */
  failed_at: string | null;
}

export const smsApi = {
  /** `GET /sms-templates` — active templates only (`SmsTemplateController::index()`). */
  templates: () => send<readonly SmsTemplate[]>('/sms-templates'),

  /** `GET /cases/{case}/messages` — that case's own history, newest-inserted first. */
  list: (caseUuid: string) => send<readonly SmsMessageRow[]>(`/cases/${caseUuid}/messages`),

  /**
   * `POST /cases/{case}/messages` — answers `202`, not `201`
   * (`CaseMessageController`'s own docblock, Task 6 ruling 1): the row
   * comes back `queued`, and nothing has reached the patient's handset by
   * the time this resolves. The caller prepends the returned row rather
   * than refetching — the API already answered the one question a
   * refetch would ask again.
   */
  send: (caseUuid: string, templateCode: string) =>
    send<SmsMessageRow>(`/cases/${caseUuid}/messages`, {
      method: 'POST',
      body: { template_code: templateCode },
    }),

  /**
   * `POST /cases/{case}/resend` — resend the case's pharmacy-visit message.
   * The API picks the template from the case's product lines; answers `202`
   * with the row `queued`, exactly like `send()` above, and `409` for a
   * closed case.
   */
  resendCase: (caseUuid: string) =>
    send<SmsMessageRow>(`/cases/${caseUuid}/resend`, { method: 'POST' }),

  /** `GET /sms-messages/{message}` — poll one message by `uuid`. */
  show: (uuid: string) => send<SmsMessageRow>(`/sms-messages/${uuid}`),

  /**
   * `POST /sms-messages` — send a template STRAIGHT TO A MOBILE NUMBER, with
   * no case (product owner: "not all messages require a case number").
   * Answers `202` with the row `queued`, exactly like `send()` above.
   *
   * `mobile` is sent as typed — the API normalises it
   * (`App\Support\Cases\Mobile::normalise()`, the rule a case's own mobile
   * goes through) and answers a malformed one with a `422` on `mobile`. A
   * template that needs a case (any declared variable) is a `422` on
   * `template_code`; `isCaseFreeTemplate()` below lets a picker not offer
   * one in the first place.
   */
  sendDirect: (mobile: string, templateCode: string) =>
    send<SmsMessageRow>('/sms-messages', {
      method: 'POST',
      body: { mobile, template_code: templateCode },
    }),

  /**
   * `GET /sms-messages` — the DIRECT-SEND log: messages sent with no case,
   * newest first, CURSOR-PAGINATED. Built on `apiInternals.request`, NOT
   * `send()` — this module's own docblock explains the trap: `send()` keeps
   * `data` and throws `meta.next_cursor` away, and this is the one SMS list
   * that pages. `mobile` is normalised server-side, so `05…` and `9665…`
   * filter identically.
   */
  direct: async (params: DirectMessageQuery = {}): Promise<SmsMessagePage> => {
    const { data, meta } = await request<readonly SmsMessageRow[]>(`/sms-messages${directQuery(params)}`);
    return { data, meta: meta as SmsMessagePage['meta'] };
  },
};

/** `GET /sms-messages`'s query — `DirectMessageIndexRequest::rules()`, verified. */
export interface DirectMessageQuery {
  mobile?: string;
  cursor?: string;
  per_page?: number;
}

/**
 * A page of `GET /sms-messages` — `SmsMessagePageEnvelope` in the API's
 * OpenAPI document, the same `{data, meta: {next_cursor, per_page, total}}`
 * shape as `SmsTemplateVersionPage` below.
 */
export interface SmsMessagePage {
  data: readonly SmsMessageRow[];
  meta: {
    next_cursor: string | null;
    per_page: number;
    total: number;
  };
}

function directQuery(params: DirectMessageQuery): string {
  const search = new URLSearchParams();
  if (params.mobile !== undefined) search.set('mobile', params.mobile);
  if (params.cursor !== undefined) search.set('cursor', params.cursor);
  if (params.per_page !== undefined) search.set('per_page', String(params.per_page));
  const built = search.toString();
  return built === '' ? '' : `?${built}`;
}

/**
 * Whether a template may be sent with NO case — exactly when it declares no
 * variables. Mirrors `App\Support\Sms\SmsRenderer::renderWithoutCase()` in
 * the API repository: every placeholder name in the closed vocabulary is
 * read from a case (or, for `{{link}}`, from the case request a `request.*`
 * send opens), so a template declaring any of them cannot be rendered for a
 * bare number and the API refuses it. A UI CONVENIENCE, not the rule — the
 * server's `422` still stands behind it.
 */
export function isCaseFreeTemplate(template: SmsTemplate): boolean {
  return Object.keys(template.variables).length === 0;
}

/**
 * `App\Support\Cases\Mobile::normalise()`, transcribed: strip everything
 * but digits, keep a leading `966`, otherwise drop leading zeros and prefix
 * `966`. Returns the normalised number only when it then has the shape the
 * API accepts (`966` + nine digits), else `null`.
 *
 * USED ONLY TO DECIDE WHEN A TYPED NUMBER IS COMPLETE ENOUGH TO FILTER THE
 * DIRECT-SEND LOG BY — never to rewrite what is sent. The API normalises
 * and validates the submitted value itself, so a disagreement here can only
 * ever cost a filter, not a wrong recipient.
 */
export function completeSaudiMobile(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  const normalised = digits.startsWith('966') ? digits : `966${digits.replace(/^0+/, '')}`;
  return /^966\d{9}$/.test(normalised) ? normalised : null;
}

/*
 * ---------------------------------------------------------------------
 * Template management (M-18, Task 6). Six endpoints, verified against the
 * API repository — `app/Support/OpenApi/ApiSpecification.php`, the four
 * `app/Http/Controllers/Api/V1/Sms/SmsTemplate*Controller.php` classes,
 * `SmsTemplateResource`/`SmsTemplateVersionResource`, and the two
 * `App\Http\Requests\Sms\{Store,Update}SmsTemplateRequest` classes — not
 * inferred from this task's own brief.
 * ---------------------------------------------------------------------
 */

/**
 * `App\Support\Sms\TemplateCodes::STRUCTURAL` in the API repository,
 * verified against that file — the twelve codes the application resolves
 * by exact string (`CaseCreator`/`SmsDispatcher`), which is why the API
 * refuses to delete, rename or deactivate any of them.
 *
 * DUPLICATED HERE, NOT FETCHED. The wire carries no `is_structural` field —
 * `SmsTemplateResource` (verified, all FIVE call sites that reuse it:
 * `SmsTemplateController::index()`, `SmsTemplateCatalogueController::
 * __invoke()`, `SmsTemplateWriteController::store()`/`::update()`, and
 * `SmsTemplateVersionController::revert()`) emits exactly nine keys, none
 * of them this one, and the OpenAPI `SmsTemplate` schema's
 * `additionalProperties: false` with that same nine-key `required` list
 * confirms it is not merely an oversight in one call site. This list is a
 * UI CONVENIENCE, not the enforcement — the server is the only real guard
 * (`SmsTemplateWriteController::destroy()`,
 * `UpdateSmsTemplateRequest::withValidator()`).
 *
 * **This copy going stale is NOT symmetric (fix round 1, M-3 — corrected;
 * the previous wording here claimed the opposite and was wrong in the
 * harmful direction).** If the server's registry ever REMOVES a code (an
 * ordinary template stops being structural), a stale copy over-protects —
 * it hides a Delete/rename/deactivate control the server would now allow,
 * which is merely a missed convenience. But if the server's registry ever
 * ADDS a new code — a genuinely new dispatch site that some future PHP
 * change resolves by name — and this copy is not updated alongside it,
 * this UI keeps offering Delete on that now-structural row, and every
 * click answers the server's `403`: exactly the "a button that always
 * fails is worse than not offering it" failure this task's brief forbids
 * for that control. This list is therefore a convenience that can go
 * stale in a way that matters, not a safe-by-construction mirror — keeping
 * it in step with `TemplateCodes::STRUCTURAL` by hand across future
 * milestones is a real, ongoing obligation, not a one-time copy.
 */
export const STRUCTURAL_TEMPLATE_CODES: ReadonlySet<string> = new Set([
  'case.pharmacy_visit.reader',
  'case.pharmacy_visit.sensor',
  'case.pharmacy_visit.both',
  'request.sensor_number',
  'request.receipt',
  'request.wasfati',
  'request.insurance_card',
  'request.error_photo',
  'request.diabetes_readings',
  'request.event_log',
  'request.wrong_sms',
  'request.serial_number',
]);

export function isStructuralTemplateCode(code: string): boolean {
  return STRUCTURAL_TEMPLATE_CODES.has(code);
}

/**
 * `POST /sms-templates`'s body — `StoreSmsTemplateRequest::rules()`,
 * verified. `variables` is sent even when empty — the validator requires
 * the KEY (`present`), not a non-empty value; omitting it entirely is a
 * `422`, distinct from sending `{}`.
 */
export interface SmsTemplateCreateInput {
  code: string;
  name: string;
  description?: string | null;
  channel: string;
  locale: string;
  body: string;
  variables: Readonly<Record<string, SmsTemplateVariableState>>;
  category?: string | null;
  is_active?: boolean;
}

/**
 * `PATCH /sms-templates/{template}`'s body — `UpdateSmsTemplateRequest::
 * rules()`, verified. Every field optional. NO `locale`/`channel` — both
 * `prohibited` server-side (sending either at all, any value, is a `422`),
 * so this type does not offer them at all rather than typing them optional
 * and hoping nobody sends one.
 */
export interface SmsTemplateUpdateInput {
  code?: string;
  name?: string;
  description?: string | null;
  body?: string;
  variables?: Readonly<Record<string, SmsTemplateVariableState>>;
  category?: string | null;
  is_active?: boolean;
}

/**
 * One row of a template's own history — `SmsTemplateVersionResource`,
 * verified field-for-field against the API repository. The `body`/
 * `variables` a save REPLACED, written before the new values were applied
 * — never the state that save produced. `change_note` is `null` for a row
 * an ordinary `PATCH` wrote, and `"Reverted to version N."` for one a
 * revert wrote.
 */
export interface SmsTemplateVersion {
  uuid: string;
  version: number;
  body: string;
  variables: Readonly<Record<string, SmsTemplateVariableState>>;
  change_note: string | null;
  created_at: string;
  created_by: number | null;
  created_by_name: string | null;
}

/**
 * `GET /sms-templates/{template}/versions` — CURSOR-PAGINATED,
 * `{data, meta: {next_cursor, per_page, total}}`, structurally identical
 * to `CasePage`/`AuditPage` (verified against `ApiSpecification.php`'s
 * `SmsTemplateVersionCollectionEnvelope`) — NOT a bare array. `per_page`
 * defaults to 50 server-side and is capped at 200; `next_cursor` is
 * `null` exactly when there is no further page.
 */
export interface SmsTemplateVersionPage {
  data: readonly SmsTemplateVersion[];
  meta: {
    next_cursor: string | null;
    per_page: number;
    total: number;
  };
}

/**
 * `?cursor=...&per_page=...`, or `''` when both are absent — the identical
 * `undefined`-drops-the-key convention `client.ts`/`cases.ts` each
 * redeclare locally for the same reason (neither is exported).
 */
function templateVersionQuery(params: { cursor?: string; per_page?: number }): string {
  const search = new URLSearchParams();
  if (params.cursor !== undefined) search.set('cursor', params.cursor);
  if (params.per_page !== undefined) search.set('per_page', String(params.per_page));
  const built = search.toString();
  return built === '' ? '' : `?${built}`;
}

export const smsTemplateManagementApi = {
  /**
   * `GET /sms-templates/catalogue` (M-18, Task 2) — the MANAGEMENT
   * listing, gated `sms.template.manage`. Every non-deleted template,
   * active OR retired, ordered by `code` — deliberately NOT
   * `smsApi.templates()` above, which stays `active()`-only and feeds the
   * send picker for a patient-safety reason unrelated to managing the
   * catalogue (see that method's own docblock and
   * `SmsTemplateCatalogueController`'s own class docblock in the API
   * repository). Same `SmsTemplate` shape either way — this reuses it
   * rather than declaring a second, identical interface.
   */
  catalogue: () => send<readonly SmsTemplate[]>('/sms-templates/catalogue'),

  /** `POST /sms-templates`. Answers `201` with the created template. */
  createTemplate: (input: SmsTemplateCreateInput) =>
    send<SmsTemplate>('/sms-templates', { method: 'POST', body: input }),

  /**
   * `PATCH /sms-templates/{template}`. A structural template refuses a
   * `code` change or `is_active: false` with a `422` keyed to that field
   * (`UpdateSmsTemplateRequest::withValidator()`) — this call does not
   * pre-empt either refusal; the caller reads `error.details` the same
   * way any other validation failure is shown.
   */
  updateTemplate: (uuid: string, input: SmsTemplateUpdateInput) =>
    send<SmsTemplate>(`/sms-templates/${uuid}`, { method: 'PATCH', body: input }),

  /**
   * `DELETE /sms-templates/{template}` — soft-deletes, `204 No Content`.
   * `send<void>()` resolves to `undefined`: `request()`'s single
   * `.json().catch(() => ({}))` path (`client.ts`'s own docblock) parses
   * the empty body to `{}`, whose absent `.data` key reads `undefined`. A
   * structural template answers `403`, thrown as the ordinary `ApiError`.
   */
  deleteTemplate: (uuid: string) => send<void>(`/sms-templates/${uuid}`, { method: 'DELETE' }),

  /**
   * `GET /sms-templates/{template}/versions` — newest first, paginated.
   * DELIBERATELY NOT `send<SmsTemplateVersionPage>()` — the identical
   * `casesApi.list()`/`api.auditLog()` trap this module's own docblock
   * warns about for `smsApi.templates()`'s SIBLING endpoints: `send()`
   * resolves to `request()`'s `.data` alone and throws `meta` away, and
   * `meta.next_cursor` is the only way a caller pages through a template's
   * history.
   */
  versions: async (
    uuid: string,
    params: { cursor?: string; per_page?: number } = {},
  ): Promise<SmsTemplateVersionPage> => {
    const { data, meta } = await request<readonly SmsTemplateVersion[]>(
      `/sms-templates/${uuid}/versions${templateVersionQuery(params)}`,
    );
    return { data, meta: meta as SmsTemplateVersionPage['meta'] };
  },

  /**
   * `POST /sms-templates/{template}/versions/{version}/revert`. `version`
   * is the per-template SEQUENCE NUMBER `SmsTemplateVersion.version`
   * carries, never a uuid. Not a rewrite — a new update, itself versioned
   * (spec D-4); the response is the template as reverted.
   */
  revert: (uuid: string, version: number) =>
    send<SmsTemplate>(`/sms-templates/${uuid}/versions/${version}/revert`, { method: 'POST' }),
};
